// PaymentMethodModal/index.tsx
import React, { useState, useEffect } from "react";
import { X } from "lucide-react";
import toast from "react-hot-toast";
import { CustomButton } from "@/components/ui/Buttons";
import {
  useAddPaymentMethodMutation,
  useUpdatePaymentMethodMutation,
} from "@/redux/feature/paymentMethod/paymentMethod";
import { handleApiRequest } from "@/utils/handleApiRequest";
import { CustomCheckbox } from "@/components/ui/Checkbox";
import {
  AccountVerificationState,
  BranchOption,
  PaymentMethodFormData,
  PaymentMethodModalProps,
} from "./type";
import { getInitialFormData } from "@/utils/PaymentMethodModalUtils";
import BasicInfoSection from "./BasicInfoSection";
import BankSelectionSection from "./BankSelectionSection";
import RoutingCodeSection from "./RoutingCodeSection";
import AccountVerificationSection from "./AccountVerificationSection";
import AddressContactSection from "./AddressContactSection";

const PaymentMethodModal: React.FC<PaymentMethodModalProps> = ({
  onClose,
  creatorId,
  isEdit = false,
  paymentMethodId,
  initialData,
}) => {
  // Form state
  const [formData, setFormData] = useState<PaymentMethodFormData>(
    initialData || getInitialFormData()
  );

  // UI state
  const [selectedCountry, setSelectedCountry] = useState("IN");
  const [loadingBranches, setLoadingBranches] = useState(false);
  const [ifscError, setIfscError] = useState("");
  const [branches, setBranches] = useState<BranchOption[] | []>([]);

  // Account verification state
  const [accountVerification, setAccountVerification] =
    useState<AccountVerificationState>({
      step: "enter",
      enteredNumber: "",
      confirmNumber: "",
      showEnteredNumber: false,
      showConfirmNumber: false,
      verified: false,
    });

  // API mutations
  const [addPaymentMethod, { isLoading: isAddLoading }] =
    useAddPaymentMethodMutation();
  const [updatePaymentMethod, { isLoading: isUpdateLoading }] =
    useUpdatePaymentMethodMutation();
  const isLoading = isAddLoading || isUpdateLoading;

  // Initialize form data
  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
      if (initialData.details.accountNumber) {
        setAccountVerification((prev) => ({
          ...prev,
          enteredNumber: initialData.details.accountNumber,
          step: "verify",
          verified: true,
        }));
      }
    }
  }, [initialData]);

  // Form handlers
  const handleFormDataChange = (
    field: string,
    value: any,
    isNestedField = false
  ) => {
    if (isNestedField) {
      setFormData((prev) => ({
        ...prev,
        details: {
          ...prev.details,
          [field]: value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const handleCountryChange = (country: string) => {
    setSelectedCountry(country);
    const countryName =
      country === "IN"
        ? "India"
        : country === "US"
        ? "United States"
        : country === "UK"
        ? "United Kingdom"
        : "";
    handleFormDataChange("country", countryName, true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!accountVerification.verified) {
      toast.error("Please verify your account number");
      return;
    }

    // Additional validation
    if (
      selectedCountry === "IN" &&
      !formData.details.routingCode.match(/^[A-Z]{4}0[A-Z0-9]{6}$/)
    ) {
      toast.error("Please enter a valid IFSC code");
      return;
    }

    const payload =
      isEdit && paymentMethodId
        ? {
            id: paymentMethodId,
            label: formData.label,
            isDefault: formData.isDefault,
            details: formData.details,
          }
        : {
            creator_id: creatorId,
            ...formData,
          };

    const mutation = isEdit ? updatePaymentMethod : addPaymentMethod;
    const successMessage = isEdit ? "updated" : "added";

    await handleApiRequest({
      payload,
      requestFunction: mutation,
      onSuccess: () => {
        toast.success(`Payment method ${successMessage} successfully!`);
        onClose();
      },
      onError: (err) => {
        const error = err as {
          data?: { errorSource?: { path?: string; message: string }[] };
        };

        if (error?.data?.errorSource) {
          error.data.errorSource.forEach((item) => {
            const message = item.path
              ? `${item.path} is ${item.message}`
              : item.message;
            toast.error(message);
          });
        } else {
          toast.error(`Failed to ${isEdit ? "update" : "add"} payment method`);
        }
      },
    });
  };

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-gray-800">
          {isEdit ? "Edit Bank Account" : "Add Bank Account"}
        </h2>
        <button
          onClick={onClose}
          className="text-gray-500 hover:text-gray-700 p-1"
          aria-label="Close"
        >
          <X size={20} />
        </button>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="space-y-6">
          {/* Basic Information */}
          <BasicInfoSection
            formData={formData}
            onFormDataChange={handleFormDataChange}
          />

          {/* Bank Selection */}
          <BankSelectionSection
            formData={formData}
            selectedCountry={selectedCountry}
            onFormDataChange={handleFormDataChange}
            onCountryChange={handleCountryChange}
          />

          {/* Routing Code */}
          <RoutingCodeSection
            formData={formData}
            selectedCountry={selectedCountry}
            branches={branches}
            loadingBranches={loadingBranches}
            ifscError={ifscError}
            onFormDataChange={handleFormDataChange}
            setBranches={setBranches}
            setLoadingBranches={setLoadingBranches}
            setIfscError={setIfscError}
          />

          {/* Account Number Verification */}
          <AccountVerificationSection
            formData={formData}
            accountVerification={accountVerification}
            setAccountVerification={setAccountVerification}
            selectedCountry={selectedCountry}
            onFormDataChange={handleFormDataChange}
          />

          {/* Address & Contact */}
          <AddressContactSection
            formData={formData}
            onFormDataChange={handleFormDataChange}
          />

          {/* Default Payment Method */}
          <div className="flex items-center">
            <CustomCheckbox
              name="isDefault"
              checked={formData.isDefault}
              onChange={(e) =>
                handleFormDataChange("isDefault", e.target.checked)
              }
            />
            <label htmlFor="isDefault" className="ml-2 text-sm text-gray-700">
              Set as default payment method
            </label>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex w-full gap-4 mt-6">
          <CustomButton
            variant="outline"
            color="#344054"
            fontSize="14px"
            fontWeight="500"
            padding="8px 24px"
            border="1px solid #E8EBEE"
            borderRadius="8px"
            className="leading-5 w-full"
            onClick={onClose}
            type="button"
          >
            Cancel
          </CustomButton>

          <CustomButton
            fontSize="14px"
            fontWeight="500"
            disabled={isLoading || !accountVerification.verified}
            padding="8px 24px"
            backgroundColor="#17457D"
            borderRadius="8px"
            className="leading-5 w-full disabled:opacity-50"
            type="submit"
          >
            {isLoading
              ? `${isEdit ? "Updating..." : "Adding..."}`
              : isEdit
              ? "Update Account"
              : "Add Account"}
          </CustomButton>
        </div>
      </form>
    </div>
  );
};

export default PaymentMethodModal;
